import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { isUnauthenticatedViewAllowed } from '../utils/accessPolicy';

export type ViewType =
  | 'home'
  | 'profile'
  | 'messages'
  | 'gigs'
  | 'gigs-coming'
  | 'subscription'
  | 'subscription-coming'
  | 'checkout'
  | 'favorites'
  | 'saved-searches'
  | 'admin'
  | 'auth'
  | 'privacy'
  | 'terms'
  | 'forgot-password'
  | 'unsubscribe'
  | 'settings'
  | 'faq'
  | 'thank-you'
  | 'angrerett'
  | 'about'
  | 'my-gigs'
  | 'my-interests'
  | 'gig-interests';

type NavContextValue = {
  currentView: ViewType;
  setCurrentView: Dispatch<SetStateAction<ViewType>>;
  previousView: ViewType;
  setPreviousView: Dispatch<SetStateAction<ViewType>>;
  selectedUserId: string | null;
  setSelectedUserId: Dispatch<SetStateAction<string | null>>;
  messageRecipientId: string | null;
  setMessageRecipientId: Dispatch<SetStateAction<string | null>>;
  navigateTo: (
    view: ViewType,
    options?: {
      userId?: string;
      recipientId?: string;
      currentSession?: any;
      saveScrollForView?: (view: ViewType) => void;
      setPageDirection?: (direction: 'left' | 'right') => void;
      replace?: boolean;
    }
  ) => void;
};

const NavContext = createContext<NavContextValue | undefined>(undefined);
const PAGE_ORDER = ['home', 'profile', 'messages', 'gigs'];

function getPageDirection(fromPage: string, toPage: string): 'left' | 'right' {
  const fromIndex = PAGE_ORDER.indexOf(fromPage);
  const toIndex = PAGE_ORDER.indexOf(toPage);
  if (fromIndex === -1 || toIndex === -1) return 'left';
  return toIndex > fromIndex ? 'right' : 'left';
}

export function NavProvider({ children }: { children: ReactNode }) {
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    // Keep existing refresh behavior: admins reopen in admin view
    const savedAdminStatus = localStorage.getItem('talentfunn_is_admin');
    return savedAdminStatus === 'true' ? 'admin' : 'home';
  });
  const [previousView, setPreviousView] = useState<ViewType>('home');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [messageRecipientId, setMessageRecipientId] = useState<string | null>(null);
  const currentViewRef = useRef<ViewType>(currentView);

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  const navigateTo = useCallback(
    (
      view: ViewType,
      options: {
        userId?: string;
        recipientId?: string;
        currentSession?: any;
        saveScrollForView?: (view: ViewType) => void;
        setPageDirection?: (direction: 'left' | 'right') => void;
        replace?: boolean;
      } = {}
    ) => {
      const fromView = currentViewRef.current;
      const { userId, recipientId, currentSession, saveScrollForView, setPageDirection, replace } = options;

      if (!currentSession && !isUnauthenticatedViewAllowed(view)) {
        view = 'home';
      }

      saveScrollForView?.(fromView);
      setPageDirection?.(getPageDirection(fromView, view));

      setPreviousView(fromView);
      setCurrentView(view);
      currentViewRef.current = view;

      if (userId !== undefined) {
        setSelectedUserId(userId);
      }

      if (recipientId !== undefined || view === 'messages') {
        setMessageRecipientId(recipientId || null);
      }

      const state = { view, userId, recipientId, fromView };
      const url = `?view=${view}${userId ? `&userId=${userId}` : ''}${recipientId ? `&recipientId=${recipientId}` : ''}`;
      if (replace) {
        window.history.replaceState(state, '', url);
      } else {
        window.history.pushState(state, '', url);
      }
    },
    []
  );

  return (
    <NavContext.Provider
      value={{
        currentView,
        setCurrentView,
        previousView,
        setPreviousView,
        selectedUserId,
        setSelectedUserId,
        messageRecipientId,
        setMessageRecipientId,
        navigateTo,
      }}
    >
      {children}
    </NavContext.Provider>
  );
}

export function useNav() {
  const context = useContext(NavContext);
  if (!context) {
    throw new Error('useNav must be used within a NavProvider');
  }
  return context;
}
